import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { AdminRole, trimDecimalZeros, UnattachedDepositStatus, UnattachedDepositSubStatus } from "@cpg/common";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { StatusBadge } from "@/components/status-badge";
import { Amount } from "@/components/amount";
import { AssetLabel } from "@/components/asset-label";
import { HashLink } from "@/components/hash-link";
import { UnattachedAmlDialog } from "@/components/unattached-aml-dialog";
import { REFUND_STATUS_COLOR, TRANSACTION_STATUS_COLOR, UNATTACHED_DEPOSIT_STATUS_COLOR } from "@/lib/status-colors";
import { formatStatusLabel } from "@/lib/utils";
import { hasRole } from "@/lib/roles";
import { ApiError } from "@/api/client";
import { getUnattachedDeposit, retryUnattachedDepositSettlement, type UnattachedAmlAction } from "@/api/unattached-deposits";
import { type UnattachedDepositDetailResponse } from "@/api/types";

// feature-037: an unattached deposit's 360° view — the deposit, its original
// invoice, the credit, the AML decision, the refunds and every related
// transaction — plus the AML officer's three actions while it is held
// (HIGH_RISK) and the INVOICES_MANAGER credit retry after SETTLEMENT_FAILED.
// Laid out like the invoice 360° page (_app.invoices_.$invoiceId.tsx): a
// header with the actions, a Summary card, then Transactions and Refunds as
// the same tables.
export const Route = createFileRoute("/_app/unattached-deposits_/$unattachedDepositId")({
  component: UnattachedDepositDetailPage,
});

// ─── formatting helpers (same as the invoice detail page) ───────────────────

const USD_FORMATTER = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });

function formatFiat(amount: string): string {
  return USD_FORMATTER.format(Number(amount));
}

function formatCrypto(amount: string | null, symbol: string): string {
  return amount === null ? "—" : `${trimDecimalZeros(amount)} ${symbol}`;
}

function formatTimestamp(value: string | null): string {
  if (!value) return "—";
  return new Date(value).toLocaleString("en-US", { dateStyle: "short", timeStyle: "short" });
}

function DetailRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="font-medium" style={{ fontVariantNumeric: "tabular-nums" }}>
        {value}
      </dd>
    </div>
  );
}

function SectionCard({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

function UnattachedDepositDetailPage() {
  const { unattachedDepositId } = Route.useParams();
  const { admin } = Route.useRouteContext();
  const queryClient = useQueryClient();
  const queryKey = ["unattached-deposit", unattachedDepositId];
  const [amlAction, setAmlAction] = useState<UnattachedAmlAction | null>(null);

  const { data: deposit, isPending, isError } = useQuery({
    queryKey,
    queryFn: () => getUnattachedDeposit(unattachedDepositId),
  });

  const onUpdated = (data: UnattachedDepositDetailResponse) => {
    queryClient.setQueryData(queryKey, data);
    void queryClient.invalidateQueries({ queryKey: ["unattached-deposits"] });
    void queryClient.invalidateQueries({ queryKey: ["aml-cases"] });
    setAmlAction(null);
  };
  const retry = useMutation({
    mutationFn: () => retryUnattachedDepositSettlement(unattachedDepositId),
    onSuccess: (data) => {
      toast.success("Credit retried — back to CREDITING.");
      onUpdated(data);
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : "Retry failed."),
  });

  if (isPending) {
    return <div className="py-10 text-center text-sm text-muted-foreground">Loading deposit…</div>;
  }
  if (isError || !deposit) {
    return <div className="py-10 text-center text-sm text-muted-foreground">Failed to load deposit.</div>;
  }

  const symbol = deposit.asset.symbol;
  const held =
    deposit.status === UnattachedDepositStatus.ACTION_REQUIRED && deposit.subStatus === UnattachedDepositSubStatus.HIGH_RISK;
  const creditFailed =
    deposit.status === UnattachedDepositStatus.ACTION_REQUIRED &&
    deposit.subStatus === UnattachedDepositSubStatus.SETTLEMENT_FAILED;
  const showAmlActions = held && hasRole(admin.roles, AdminRole.AML_OFFICER);
  const showRetryButton = creditFailed && hasRole(admin.roles, AdminRole.INVOICES_MANAGER);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">{deposit.publicId}</h2>
          <p className="text-sm text-muted-foreground">
            <Link to="/merchants/$merchantId" params={{ merchantId: deposit.merchant.id }} className="hover:underline">
              {deposit.merchant.businessName}
            </Link>{" "}
            · {deposit.merchant.publicId}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <StatusBadge
            label={formatStatusLabel(deposit.subStatus ?? deposit.status)}
            color={UNATTACHED_DEPOSIT_STATUS_COLOR[deposit.status]}
          />
          {showAmlActions && (
            <>
              <Button type="button" size="sm" onClick={() => setAmlAction("accept")}>
                Accept
              </Button>
              <Button type="button" size="sm" variant="outline" onClick={() => setAmlAction("refund")}>
                Refund
              </Button>
              <Button type="button" size="sm" variant="destructive" onClick={() => setAmlAction("reject")}>
                Reject
              </Button>
            </>
          )}
          {showRetryButton && (
            <Button type="button" size="sm" disabled={retry.isPending} onClick={() => retry.mutate()}>
              {retry.isPending ? "Retrying…" : "Retry credit"}
            </Button>
          )}
        </div>
      </div>

      <SectionCard title="Summary">
        <div className="mb-4 flex flex-wrap items-end justify-between gap-4 border-b pb-4">
          <div>
            <div className="text-2xl font-semibold tracking-tight">
              <Amount native={deposit.cryptoAmount} symbol={symbol} inline />
            </div>
            {deposit.credit && (
              <div className="mt-0.5 text-sm text-muted-foreground">{formatFiat(deposit.credit.cryptoAmountUsd)}</div>
            )}
          </div>
          <AssetLabel symbol={symbol} network={deposit.asset.network} inline />
        </div>
        <dl className="space-y-2 text-sm">
          <DetailRow
            label="Original invoice"
            value={
              <Link
                to="/invoices/$invoiceId"
                params={{ invoiceId: deposit.invoice.id }}
                className="font-mono hover:underline"
              >
                {deposit.invoice.publicId}
              </Link>
            }
          />
          <DetailRow
            label="Sender"
            value={<HashLink value={deposit.senderAddress} type="address" network={deposit.asset.network} />}
          />
          <DetailRow label="Moving" value={deposit.activeMovement ? formatStatusLabel(deposit.activeMovement) : "—"} />
          <DetailRow label="Detected" value={formatTimestamp(deposit.detectedAt)} />
          <DetailRow label="Confirmed" value={formatTimestamp(deposit.confirmedAt)} />
          <DetailRow label="Credit cycle started" value={formatTimestamp(deposit.settlingAt)} />
          <DetailRow label="Resolved" value={formatTimestamp(deposit.resolvedAt)} />
          <DetailRow label="Resolved by" value={deposit.resolvedBy ? formatStatusLabel(deposit.resolvedBy) : "—"} />
        </dl>
      </SectionCard>

      {deposit.credit && (
        <SectionCard title="Credit">
          <dl className="space-y-2 text-sm">
            <DetailRow
              label="Fee"
              value={<Amount native={deposit.credit.cryptoFee} symbol={symbol} usd={deposit.credit.feeUsd} inline />}
            />
            <DetailRow label="Amount (USD)" value={formatFiat(deposit.credit.cryptoAmountUsd)} />
            <DetailRow label="Rate" value={`1 ${symbol} = ${formatFiat(deposit.credit.usdRate)}`} />
            <DetailRow label="Priced at" value={formatTimestamp(deposit.credit.pricedAt)} />
          </dl>
        </SectionCard>
      )}

      {deposit.aml && (
        <SectionCard title="AML resolution">
          <dl className="space-y-2 text-sm">
            <DetailRow label="Resolved by" value={<span className="font-mono text-xs">{deposit.aml.resolvedBy}</span>} />
            <DetailRow label="Resolved at" value={formatTimestamp(deposit.aml.resolvedAt)} />
            <DetailRow label="Action" value={formatStatusLabel(deposit.aml.action)} />
            <DetailRow label="Note" value={deposit.aml.note ?? "—"} />
          </dl>
        </SectionCard>
      )}

      <SectionCard title="Transactions">
        {deposit.transactions.length === 0 ? (
          <p className="text-sm text-muted-foreground">No transactions.</p>
        ) : (
          <div className="overflow-hidden overflow-x-auto rounded-lg border">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-muted text-left font-semibold text-muted-foreground">
                  <th className="px-3 py-2">Operation</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2 text-right">Amount</th>
                  <th className="px-3 py-2 text-right">Network fee</th>
                  <th className="px-3 py-2">Tx hash</th>
                  <th className="px-3 py-2">Counterparty</th>
                  <th className="px-3 py-2">Last event</th>
                </tr>
              </thead>
              <tbody>
                {deposit.transactions.map((tx) => (
                  <tr key={tx.id} className="border-t">
                    <td className="px-3 py-2 font-medium">{formatStatusLabel(tx.operation)}</td>
                    <td className="px-3 py-2">
                      <StatusBadge label={formatStatusLabel(tx.status)} color={TRANSACTION_STATUS_COLOR[tx.status]} />
                    </td>
                    <td className="px-3 py-2 text-right" style={{ fontVariantNumeric: "tabular-nums" }}>
                      {formatCrypto(tx.amount, tx.asset.symbol)}
                    </td>
                    <td className="px-3 py-2 text-right" style={{ fontVariantNumeric: "tabular-nums" }}>
                      {formatCrypto(tx.networkFee, tx.feeCurrency ?? tx.asset.symbol)}
                    </td>
                    <td className="px-3 py-2">
                      <HashLink value={tx.txHash} type="tx" network={tx.asset.network} />
                    </td>
                    <td className="px-3 py-2">
                      <HashLink value={tx.counterpartyAddress} type="address" network={tx.asset.network} />
                    </td>
                    <td className="px-3 py-2 text-muted-foreground">{formatTimestamp(tx.lastEventAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      <SectionCard title="Refunds">
        {deposit.refunds.length === 0 ? (
          <p className="text-sm text-muted-foreground">No refunds.</p>
        ) : (
          <div className="overflow-hidden overflow-x-auto rounded-lg border">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-muted text-left font-semibold text-muted-foreground">
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Initiator</th>
                  <th className="px-3 py-2">Claimant</th>
                  <th className="px-3 py-2 text-right">Amount</th>
                  <th className="px-3 py-2">Address</th>
                  <th className="px-3 py-2">Created</th>
                  <th className="px-3 py-2">Claimed</th>
                </tr>
              </thead>
              <tbody>
                {deposit.refunds.map((refund) => (
                  <tr key={refund.id} className="border-t">
                    <td className="px-3 py-2">
                      <StatusBadge label={formatStatusLabel(refund.status)} color={REFUND_STATUS_COLOR[refund.status]} />
                    </td>
                    <td className="px-3 py-2 text-muted-foreground">{formatStatusLabel(refund.initiator)}</td>
                    <td className="px-3 py-2 text-muted-foreground">
                      {refund.claimant ? formatStatusLabel(refund.claimant) : "—"}
                    </td>
                    <td className="px-3 py-2 text-right" style={{ fontVariantNumeric: "tabular-nums" }}>
                      {formatCrypto(refund.amount, symbol)}
                    </td>
                    <td className="px-3 py-2">
                      <HashLink value={refund.address} type="address" network={deposit.asset.network} />
                    </td>
                    <td className="px-3 py-2 text-muted-foreground">{formatTimestamp(refund.createdAt)}</td>
                    <td className="px-3 py-2 text-muted-foreground">{formatTimestamp(refund.claimedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      <UnattachedAmlDialog
        depositId={deposit.id}
        action={amlAction}
        onOpenChange={(open) => !open && setAmlAction(null)}
        onSuccess={onUpdated}
      />
    </div>
  );
}
